// ─────────────────────────────────────────────────────────────────────────────
// Sistema visual COMPARTIDO de los dos libros de Reconocimiento — «Brillamos Juntos»
// (`brillamosExcel.ts`) y «Reconocimiento Diario» (`dailyExcel.ts`).
//
// Las dos plantillas del cliente (`02 - Campana brillamos juntos.xlsx` y
// `01 - Reconocimiento Diario.xlsx`) son el MISMO diseño medido celda a celda: banner teal de
// título + subtítulo, bloque de metadatos celeste, encabezado de tabla teal con «Ajustar texto»,
// banding zebra blanco/celeste, rejilla de bordes finos y los mismos anchos de columna. Tres de sus
// hojas —«Embudo Adopción», los dos ranking y «Todos los reconocimientos»— son además idénticas en
// estructura, así que viven aquí completas en vez de duplicarse en los dos módulos.
//
// Lo que NO vive aquí es lo que cada reporte dice de sí mismo: su Resumen Ejecutivo (KPIs distintos
// y en distinto orden) y su glosario de «Definiciones». Cada libro los arma en su propio módulo.
//
// Cada builder recibe un `Traductor` ya acotado al bloque i18n de su reporte (`reports.shine` o
// `reports.daily`), así que las claves `excel.*` que se resuelven aquí existen en los dos con el
// mismo nombre y cada libro sale en el idioma de quien descarga.
// ─────────────────────────────────────────────────────────────────────────────
import { buildMatrizHabitosHeader, desplazarMerge, mergeDeFila } from '@/utils/exportExcelMultiSheet';
import type {
  ExcelCellStyleHint, ExcelCellValue, ExcelColumnStyleHint, ExcelSheetSpec, ExcelSheetTheme,
} from '@/utils/exportExcelMultiSheet';
import { excelColors } from '@/utils/excelTheme';
import type { FunnelStageDto, NominationRawExportRowDto } from '@/api/recognition';
import { celdasDeHabitos } from '../../fundamentalesHabitosMatrix';
import type { FundamentalConHabitosResueltos } from '../../fundamentalesHabitosMatrix';

/** Guion de «sin dato»: nunca un 0, que afirmaría un hecho que no consta. */
export const SIN_DATO = '—';

export const FORMATO_ENTERO = '#,##0';
export const FORMATO_PORCENTAJE = '0.00%';
export const FORMATO_TASA = '0.0%';

/** Alto en puntos del banner de título, medido de las dos plantillas. */
export const ALTO_TITULO = 23.4;
/** Alineación vertical compartida por todas las celdas de datos de estos libros. */
export const CENTRADO_VERTICAL = 'middle' as const;

/** Fila 0-indexed del encabezado de «Embudo Adopción» y de las dos hojas de ranking. */
export const FILA_HEADER_TABLA_SIMPLE = 3;
/** Filas de banner que «Todos los reconocimientos» antepone a su matriz de hábitos. */
const FILAS_BANNER_MATRIZ = 2;

const TOTAL_COLUMNAS_EMBUDO = 5;
const TOTAL_COLUMNAS_RANKING = 11;
/** Ancho de una hoja de hueco documentado: título, subtítulo y nada más que pintar. */
const ANCHOS_HOJA_SIN_DATOS = [48.56, 15.33, 21];

/** A y B salen de la plantilla; C/D/E no tienen ancho medido y se dimensionan por su encabezado. */
const ANCHOS_EMBUDO = [41.89, 17.11, 16, 20, 18];

/**
 * «Top Reconocidos»/«Top Reconocedores». Las plantillas miden sus columnas contra datos de demo
 * cortos; aquí se amplían las de texto libre (Nombre, Correo, Área, Fundamental) y se dimensionan
 * las que quedaron sin medir (Numero Nómina, Site, Cross-OU, Tipo dominante): con datos reales
 * —correos, nombres de OU— el ancho de la plantilla deja el archivo ilegible.
 */
const ANCHOS_RANKING = [6.66, 26, 34, 18, 24, 16, 18, 34, 16, 14, 20];

/**
 * Paleta de las hojas: la teal de `excelColors`, medida de estas mismas plantillas. Se pasa como
 * `styleHints.theme` (y no con `isDefaultThemeSkipped` + `cellStyles`) porque las plantillas sí usan
 * banding zebra en sus tablas: dejar que el tema lo pinte mantiene el costo del hint en
 * O(columnas) en vez de O(filas × columnas), y «Todos los reconocimientos» puede traer 5000 filas.
 */
export const TEMA_PLANTILLA_TEAL: ExcelSheetTheme = {
  tituloFondo: excelColors.tituloFondo,
  tituloTexto: excelColors.tituloTexto,
  subtituloFondo: excelColors.subtituloFondo,
  subtituloTexto: excelColors.subtituloTexto,
  headerTablaFondo: excelColors.headerTablaFondo,
  headerTablaTexto: excelColors.headerTablaTexto,
  filaPar: excelColors.filaPar,
  filaImpar: excelColors.filaImpar,
  isHeaderWrapped: true,
  isSubtituloItalic: true,
};

/**
 * Resuelve un texto del namespace `recognition` bajo el bloque del reporte que llame
 * (`reports.shine` o `reports.daily`). Se recibe como parámetro y no se importa el singleton de
 * i18n: así el módulo no depende de él y una prueba puede pasar su propio traductor.
 */
export type Traductor = (clave: string, opciones?: Record<string, unknown>) => string;

/** Una fila del glosario de «Definiciones», con sus tres celdas ya traducidas. */
export interface FilaDefinicionGlosario {
  dato: string;
  queEs: string;
  comoSeCalcula: string;
}

/** Una fila de «Top Reconocidos»/«Top Reconocedores», con el texto ya resuelto en el idioma activo. */
export interface FilaRankingPlantilla {
  nombre: string;
  isSuprimido: boolean;
  email?: string | null;
  employeeId?: string | null;
  area?: string | null;
  ouName?: string | null;
  site?: string | null;
  fundamental?: string | null;
  count: number;
  crossOu: string;
  tipoDominante?: string | null;
}

/** Banner de una hoja: título teal a 18 pt y subtítulo celeste en cursiva, ambos a lo ancho. */
export function estilosDeBanner(anchoEnColumnas: number): {
  cellStyles: ExcelCellStyleHint[];
  merges: string[];
  rowHeights: Array<{ row: number; height: number }>;
} {
  return {
    cellStyles: [
      { row: 0, column: 0, fontSize: 18, horizontal: 'left', vertical: CENTRADO_VERTICAL, indent: 1 },
      { row: 1, column: 0, horizontal: 'left', vertical: CENTRADO_VERTICAL, indent: 1, hasWrapText: true },
    ],
    merges: [mergeDeFila(0, 0, anchoEnColumnas - 1), mergeDeFila(1, 0, anchoEnColumnas - 1)],
    rowHeights: [{ row: 0, height: ALTO_TITULO }],
  };
}

/** Columna de texto de una tabla de datos: a la izquierda, centrada en vertical y ajustada. */
export function columnaDeTexto(columna: number): ExcelColumnStyleHint {
  return {
    column: columna,
    fontColorArgb: excelColors.textoCuerpo,
    horizontal: 'left',
    vertical: CENTRADO_VERTICAL,
    hasWrapText: true,
  };
}

/** Columna corta y centrada (OU, Site, Cross-OU): su contenido es una etiqueta, no prosa. */
export function columnaCentrada(columna: number): ExcelColumnStyleHint {
  return {
    column: columna,
    fontColorArgb: excelColors.textoCuerpo,
    horizontal: 'center',
    vertical: CENTRADO_VERTICAL,
  };
}

/** Columna numérica: a la derecha y con formato nativo de Excel. */
export function columnaNumerica(columna: number, numFmt: string): ExcelColumnStyleHint {
  return {
    column: columna,
    fontColorArgb: excelColors.textoCuerpo,
    horizontal: 'right',
    vertical: CENTRADO_VERTICAL,
    numFmt,
  };
}

/** Rejilla de bordes finos sobre una tabla completa (encabezado incluido). */
export function rejillaDeTabla(filaInicio: number, filaFin: number, columnaFin: number) {
  return {
    startRow: filaInicio,
    endRow: filaFin,
    startColumn: 0,
    endColumn: columnaFin,
    colorArgb: excelColors.bordeTabla,
  };
}

/**
 * Bloque de metadatos: etiqueta en negrita índigo y valor en gris, ambos sobre el celeste de la
 * plantilla. Quien lo llame combina el valor hasta el final del banner (un filtro con varias OU no
 * cabe en el ancho de la columna «Valor»).
 */
export function estilosDeMetadatos(filas: number[]): ExcelCellStyleHint[] {
  return filas.flatMap((fila): ExcelCellStyleHint[] => [
    {
      row: fila,
      column: 0,
      fillArgb: excelColors.metaFondo,
      fontColorArgb: excelColors.metaEtiqueta,
      isBold: true,
      fontSize: 10,
      vertical: CENTRADO_VERTICAL,
    },
    {
      row: fila,
      column: 1,
      fillArgb: excelColors.metaFondo,
      fontColorArgb: excelColors.metaValor,
      fontSize: 10,
      hasWrapText: true,
      vertical: CENTRADO_VERTICAL,
    },
  ]);
}

/**
 * Hoja de hueco documentado (sin fuente de datos): nunca vacía, nunca con datos fabricados. Conserva
 * el banner del resto del libro para que no parezca una pestaña rota.
 */
export function buildHojaSinDatos(nombre: string, titulo: string, mensaje: string): ExcelSheetSpec {
  const banner = estilosDeBanner(ANCHOS_HOJA_SIN_DATOS.length);
  return {
    name: nombre,
    rows: [[titulo], [mensaje]],
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      columnWidths: ANCHOS_HOJA_SIN_DATOS,
      rowHeights: banner.rowHeights,
      merges: banner.merges,
      cellStyles: banner.cellStyles,
    },
  };
}

/**
 * Hoja «Embudo Adopción»: 5 columnas (`Segmento | % del total | Personas | Administrativos |
 * Operativos`) — las dos últimas con el guion de «sin dato» si el padrón no respondió (`null`),
 * nunca un 0 que afirmaría «nadie de ese segmento». «% del total» es NÚMERO con formato nativo
 * (`0.00%`) más una barra de datos nativa (Formato Condicional, gradiente verde), igual que las
 * plantillas: una barra necesita un número real para calcular su longitud, un texto no sirve.
 *
 * `subtitulo` es opcional porque los dos reportes lo redactan distinto: Brillamos mide el periodo de
 * la campaña y el Diario su propia ventana mensual, con las fechas interpoladas.
 */
export function buildEmbudoSheet(
  traducir: Traductor, embudo: FunnelStageDto[], subtitulo?: string,
): ExcelSheetSpec {
  const banner = estilosDeBanner(TOTAL_COLUMNAS_EMBUDO);
  const primeraFilaDato = FILA_HEADER_TABLA_SIMPLE + 1;
  const ultimaFilaDato = primeraFilaDato + embudo.length - 1;
  const COLUMNA_PCT = 1;

  return {
    name: traducir('excel.sheetFunnel'),
    rows: [
      [traducir('excel.funnelTitle')],
      [subtitulo ?? traducir('excel.funnelSubtitle')],
      [],
      [
        traducir('excel.colSegment'), traducir('excel.colPercentOfTotal'), traducir('excel.colPeople'),
        traducir('excel.colAdministrative'), traducir('excel.colOperational'),
      ],
      ...embudo.map((etapa): ExcelCellValue[] => [
        etapa.etapa,
        etapa.pct != null ? etapa.pct / 100 : null,
        etapa.count,
        etapa.administrativos ?? SIN_DATO,
        etapa.operativos ?? SIN_DATO,
      ]),
    ],
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILA_HEADER_TABLA_SIMPLE],
      columnWidths: ANCHOS_EMBUDO,
      rowHeights: banner.rowHeights,
      merges: banner.merges,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        columns: [
          columnaDeTexto(0),
          columnaNumerica(COLUMNA_PCT, FORMATO_PORCENTAJE),
          columnaNumerica(2, FORMATO_ENTERO),
          columnaNumerica(3, FORMATO_ENTERO),
          columnaNumerica(4, FORMATO_ENTERO),
        ],
      },
      dataBars: [{
        column: COLUMNA_PCT,
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        color: excelColors.dataBarVerde,
      }],
      cellStyles: banner.cellStyles,
      borderedRanges: [
        rejillaDeTabla(FILA_HEADER_TABLA_SIMPLE, ultimaFilaDato, TOTAL_COLUMNAS_EMBUDO - 1),
      ],
    },
  };
}

/**
 * Hoja «Top Reconocidos»/«Top Reconocedores»: las 11 columnas EXACTAS de las plantillas (`# |
 * Nombre | Correo | Numero Nómina | Área | OU | Site | Fundamental | Reconocimientos | Cross-OU |
 * Tipo dominante`). Enmascara a las personas suprimidas por el umbral de anonimato, igual que la
 * tabla en pantalla.
 */
export function buildRankingSheet(
  traducir: Traductor, nombreHoja: string, titulo: string, filas: FilaRankingPlantilla[],
): ExcelSheetSpec {
  const banner = estilosDeBanner(TOTAL_COLUMNAS_RANKING);
  const primeraFilaDato = FILA_HEADER_TABLA_SIMPLE + 1;
  const ultimaFilaDato = primeraFilaDato + filas.length - 1;
  const oculto = traducir('excel.masked');

  return {
    name: nombreHoja,
    rows: [
      [titulo],
      [traducir('excel.rankingSubtitle')],
      [],
      [
        traducir('excel.colRank'), traducir('colName'), traducir('colEmail'),
        traducir('colEmployeeId'), traducir('excel.colArea'), traducir('colOu'),
        traducir('excel.colSite'), traducir('excel.colFundamental'), traducir('colRecognitions'),
        traducir('colCrossOu'), traducir('colDominantType'),
      ],
      ...filas.map((fila, indice): ExcelCellValue[] => [
        indice + 1,
        fila.isSuprimido ? oculto : fila.nombre,
        fila.isSuprimido ? SIN_DATO : (fila.email ?? SIN_DATO),
        fila.isSuprimido ? SIN_DATO : (fila.employeeId ?? SIN_DATO),
        fila.isSuprimido ? SIN_DATO : (fila.area ?? SIN_DATO),
        fila.isSuprimido ? SIN_DATO : (fila.ouName ?? SIN_DATO),
        fila.isSuprimido ? SIN_DATO : (fila.site ?? SIN_DATO),
        fila.isSuprimido ? SIN_DATO : (fila.fundamental ?? SIN_DATO),
        fila.isSuprimido ? SIN_DATO : fila.count,
        fila.isSuprimido ? SIN_DATO : fila.crossOu,
        fila.isSuprimido ? SIN_DATO : (fila.tipoDominante ?? SIN_DATO),
      ]),
    ],
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILA_HEADER_TABLA_SIMPLE],
      columnWidths: ANCHOS_RANKING,
      rowHeights: banner.rowHeights,
      merges: banner.merges,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        columns: [
          { ...columnaCentrada(0), numFmt: '0' },
          { ...columnaDeTexto(1), isBold: true },
          columnaDeTexto(2),
          columnaDeTexto(3),
          columnaDeTexto(4),
          columnaCentrada(5),
          columnaCentrada(6),
          columnaDeTexto(7),
          columnaNumerica(8, FORMATO_ENTERO),
          columnaCentrada(9),
          columnaDeTexto(10),
        ],
      },
      cellStyles: banner.cellStyles,
      borderedRanges: [
        rejillaDeTabla(FILA_HEADER_TABLA_SIMPLE, ultimaFilaDato, TOTAL_COLUMNAS_RANKING - 1),
      ],
    },
  };
}

/** Las 24 columnas fijas de la izquierda de «Todos los reconocimientos», con su ancho propio. */
const COLUMNAS_IZQUIERDA: Array<[string, number]> = [
  ['Nominee_Name', 26], ['Nominee_Email', 32], ['Nominee_EmployeeId', 16], ['Nominee_OU', 16],
  ['Nominee_BU', 16], ['Nominee_Site', 16], ['Nominee_Department', 24],
  ['OnBehalfOf_Name', 26], ['OnBehalfOf_Email', 32], ['OnBehalfOf_EmployeeId', 16], ['OnBehalfOf_OU', 16],
  ['OnBehalfOf_BU', 16], ['OnBehalfOf_Site', 16], ['OnBehalfOf_Department', 24],
  ['Candidate_Name', 26], ['Candidate_Email', 32], ['Candidate_EmployeeId', 16], ['Candidate_OU', 16],
  ['Candidate_BU', 16], ['Candidate_Site', 16], ['Candidate_Department', 24],
  ['Date', 12], ['Año', 9], ['Mes', 9],
];

/** Las 13 columnas fijas de la derecha, con su ancho propio. */
const COLUMNAS_DERECHA: Array<[string, number]> = [
  ['Comment', 45], ['Insignia', 18], ['comes from Fundamentals', 16], ['sending a report', 14],
  ['received a report', 14], ['Status', 14], ['Migrated', 12], ['Recognition type', 18],
  ['Nominee_Role', 20], ['Candidate_Role', 20], ['Nominee_Segment', 18], ['Candidate_Segment', 18],
  ['Reco_ID', 18],
];

/** Ancho de cada columna de la matriz de hábitos: el encabezado va ajustado a la celda. */
const ANCHO_COLUMNA_HABITO = 16;

/**
 * Hoja «Todos los reconocimientos»: el export CRUDO paginado (hasta 5000 filas), una fila por
 * CANDIDATO. Los nombres de las 37 columnas fijas NO se traducen: el comité arma tablas dinámicas
 * contra estos encabezados literales, y traducirlos rompería sus libros al cambiar de idioma. Sí se
 * traducen el banner y la matriz de hábitos, que sale del catálogo en el idioma activo.
 *
 * El bloque OnBehalfOf usa `''` (no el guion de «sin dato») para sus 7 campos: `null` ahí significa
 * «no delegado» (dato correcto), no «dato ausente» — a diferencia de Nominee/Candidate, donde `null`
 * sí significa «no se pudo resolver».
 *
 * Esta hoja NO enmascara suprimidos, a diferencia de «Top Reconocidos»: reusa el mismo export crudo
 * que ya alimenta a `AdminWinners.tsx` — el umbral de anonimato aplica a los reportes AGREGADOS, no
 * a esta fuente cruda de comité.
 *
 * SIN rejilla de bordes ni estilos por celda: 5000 filas × 60 columnas son 300 000 celdas, y pintar
 * cada una una por una haría la descarga inviable. El banding zebra del tema, que se aplica por
 * fila, sí llega hasta el final.
 */
export function buildTodosLosReconocimientosSheet(
  traducir: Traductor,
  nominaciones: NominationRawExportRowDto[],
  fundamentalesConHabitos: FundamentalConHabitosResueltos[],
): ExcelSheetSpec {
  const { headerRows: matrizHeader, merges: matrizMerges } = buildMatrizHabitosHeader(fundamentalesConHabitos);
  const totalHabitos = matrizHeader[1].length;

  const filaHeaderFundamentales: ExcelCellValue[] = [
    ...COLUMNAS_IZQUIERDA.map(() => null), ...matrizHeader[0], ...COLUMNAS_DERECHA.map(() => null),
  ];
  const filaHeaderColumnas: ExcelCellValue[] = [
    ...COLUMNAS_IZQUIERDA.map(([nombre]) => nombre),
    ...matrizHeader[1],
    ...COLUMNAS_DERECHA.map(([nombre]) => nombre),
  ];

  const filas = nominaciones.map((nominacion): ExcelCellValue[] => [
    nominacion.nomineeName, nominacion.nomineeEmail ?? SIN_DATO, nominacion.nomineeEmployeeId ?? SIN_DATO,
    nominacion.nomineeOu ?? SIN_DATO, nominacion.nomineeBu ?? SIN_DATO,
    nominacion.nomineeSite ?? SIN_DATO, nominacion.nomineeDepartment ?? SIN_DATO,
    nominacion.onBehalfOfName ?? '', nominacion.onBehalfOfEmail ?? '', nominacion.onBehalfOfEmployeeId ?? '',
    nominacion.onBehalfOfOu ?? '', nominacion.onBehalfOfBu ?? '',
    nominacion.onBehalfOfSite ?? '', nominacion.onBehalfOfDepartment ?? '',
    nominacion.candidateName, nominacion.candidateEmail ?? SIN_DATO, nominacion.candidateEmployeeId ?? SIN_DATO,
    nominacion.candidateOu ?? SIN_DATO, nominacion.candidateBu ?? SIN_DATO,
    nominacion.candidateSite ?? SIN_DATO, nominacion.candidateDepartment ?? SIN_DATO,
    nominacion.date.slice(0, 10), nominacion.anio, nominacion.mes,
    ...celdasDeHabitos(nominacion, fundamentalesConHabitos),
    nominacion.comment ?? '', nominacion.badgeDailyStreak ?? '', nominacion.fromDayToDayCampaign ? 'X' : '',
    nominacion.sendingAReport ? 'X' : '', nominacion.receivedAReport ? 'X' : '',
    nominacion.status, nominacion.migrated ? 'X' : '', nominacion.tipo,
    nominacion.nomineeRole ?? SIN_DATO, nominacion.candidateRole ?? SIN_DATO,
    nominacion.audienceNominee ?? SIN_DATO, nominacion.audienceCandidate ?? SIN_DATO,
    SIN_DATO,
  ]);

  const totalColumnas = COLUMNAS_IZQUIERDA.length + totalHabitos + COLUMNAS_DERECHA.length;
  const banner = estilosDeBanner(totalColumnas);
  // Los merges de la matriz llegan relativos a `A1`: hay que bajarlos las dos filas del banner y
  // correrlos las 24 columnas fijas de la izquierda para dejarlos en su sitio real.
  const mergesMatriz = matrizMerges.map(
    (rango) => desplazarMerge(rango, COLUMNAS_IZQUIERDA.length, FILAS_BANNER_MATRIZ),
  );

  return {
    name: traducir('excel.sheetAllRecognitions'),
    rows: [
      [traducir('excel.allRecognitionsTitle')],
      [traducir('excel.allRecognitionsSubtitle')],
      filaHeaderFundamentales,
      filaHeaderColumnas,
      ...filas,
    ],
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILAS_BANNER_MATRIZ, FILAS_BANNER_MATRIZ + 1],
      columnWidths: [
        ...COLUMNAS_IZQUIERDA.map(([, ancho]) => ancho),
        ...Array.from({ length: totalHabitos }, () => ANCHO_COLUMNA_HABITO),
        ...COLUMNAS_DERECHA.map(([, ancho]) => ancho),
      ],
      rowHeights: banner.rowHeights,
      merges: [...banner.merges, ...mergesMatriz],
      cellStyles: banner.cellStyles,
    },
  };
}
